// ProjectTreePanel
// ----------------
// Shared Building → Category → Subcategory → Floor → Unit navigation panel,
// backed by /api/project-tree/:projectId (see backend/server.js). The same
// tree also carries migration-008 milestone rollups on each node so we can
// inline tier-coloured "3/12 milestones" chips next to the task counts.
//
// Designed to be drop-in for any view that wants the planning-style left
// sidebar. Currently used by TasksView; will replace the inline tree in
// ConstructionPlanningInterface in a follow-up.
//
// Contract:
//   * `projectId`      — null means "no project selected" → empty state.
//   * `selectedKey`    — dot-path key identifying the currently-selected
//                        node (e.g. "<buildingId>.<categoryId>...").
//                        `null` = "root" (whole project).
//   * `onSelect(key, filters, path)` fires whenever the user clicks a node,
//                        including the virtual "All Buildings" root.
//
// `filters` is a partial AdminTaskFilters payload — the consumer merges it
// into whatever other filter state it owns (status, search, etc.).

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Building, Box, ChevronDown, ChevronRight, FileText, Flag, Folder,
    Layers, MapPin, Target, Trophy, Wrench,
} from 'lucide-react';
import { api } from '../../lib/api';
import type { MilestoneNodeStats, ProjectTreeNode } from './types';

// Filter payload emitted on node selection. Kept structural (not typed to
// AdminTaskFilters) so the tree stays usable by any consumer that speaks
// building_id / category_id / ... — currently Tasks; Planning will follow.
export interface TreeNodeFilters {
    buildingId?: string;
    categoryId?: string;
    subcategoryId?: string;
    floorId?: string;
    unitId?: string;
}

export interface ProjectTreePanelProps {
    projectId: string | null;
    selectedKey?: string | null;
    onSelect: (
        key: string | null,
        filters: TreeNodeFilters,
        path: string[],
    ) => void;
    className?: string;
    /** Optional header shown above the building dropdown (e.g. project picker). */
    header?: React.ReactNode;
}

// ----- helpers --------------------------------------------------------------

const ICONS = {
    building:     Building,
    category:     Wrench,
    subcategory:  Layers,
    floor:        Box,
    unit:         MapPin,
} as const;

// Tier precedence: Trophy (all achieved) > Flag (has major) > Folder (minor)
// > Target (work-only). Colours echo the Planning-view badges so the two
// experiences read identically.
function pickMilestoneBadge(stats: MilestoneNodeStats | undefined) {
    if (!stats || stats.total === 0) return null;
    const allAchieved = stats.achieved === stats.total;
    if (allAchieved) {
        return { Icon: Trophy, cls: 'text-emerald-700 bg-emerald-50 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' };
    }
    if (stats.hasMajor) {
        return { Icon: Flag, cls: 'text-indigo-700 bg-indigo-50 border-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800' };
    }
    if (stats.hasMinor) {
        return { Icon: Folder, cls: 'text-amber-700 bg-amber-50 border-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800' };
    }
    return { Icon: Target, cls: 'text-blue-600 bg-blue-50 border-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' };
}

function MilestoneBadge({ stats }: { stats?: MilestoneNodeStats }) {
    const picked = pickMilestoneBadge(stats);
    if (!picked || !stats) return null;
    const { Icon, cls } = picked;
    const tierLabel = stats.hasMajor ? ' (major)' : stats.hasMinor ? ' (minor)' : '';
    return (
        <span
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded border ${cls}`}
            title={`${stats.achieved}/${stats.total} milestone${stats.total === 1 ? '' : 's'}${tierLabel}`}
        >
            <Icon className="w-2.5 h-2.5" />
            {stats.achieved}/{stats.total}
        </span>
    );
}

// Derive the partial filter payload for a node from its type. Child nodes
// inherit their ancestors' filters, so we merge `parentFilters` in.
function buildFilters(
    node: ProjectTreeNode,
    parentFilters: TreeNodeFilters,
): TreeNodeFilters {
    const next: TreeNodeFilters = { ...parentFilters };
    switch (node.type) {
        case 'building':    next.buildingId    = node.id; break;
        case 'category':    next.categoryId    = node.id; break;
        case 'subcategory': next.subcategoryId = node.id; break;
        case 'floor':       next.floorId       = node.id; break;
        case 'unit':        next.unitId        = node.id; break;
    }
    return next;
}

// ----- component ------------------------------------------------------------

export default function ProjectTreePanel({
    projectId,
    selectedKey = null,
    onSelect,
    className = '',
    header,
}: ProjectTreePanelProps) {
    const [tree, setTree]       = useState<ProjectTreeNode[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState<string | null>(null);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [buildingFilter, setBuildingFilter] = useState<string>('All');

    useEffect(() => {
        if (!projectId) { setTree([]); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.getProjectTree(projectId)
            .then((rows) => {
                if (cancelled) return;
                setTree(rows);
                // Expand the first building by default so the user sees
                // at least one level of depth without clicking.
                const next: Record<string, boolean> = {};
                if (rows[0]) next[rows[0].id] = true;
                setExpanded(next);
                setBuildingFilter('All');
            })
            .catch((e: unknown) => {
                if (cancelled) return;
                setError(e instanceof Error ? e.message : 'Failed to load tree');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [projectId]);

    // Drop-down filter: mirrors the Planning view's "Building Filter" so
    // the tree doesn't flood the panel with 10+ top-level buildings when
    // the user only cares about one.
    const filteredTree = useMemo(() => {
        if (buildingFilter === 'All') return tree;
        return tree.filter((b) => b.name === buildingFilter);
    }, [tree, buildingFilter]);

    const toggle = useCallback((key: string) => {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const selectRoot = useCallback(() => {
        onSelect(null, {}, ['All Buildings']);
    }, [onSelect]);

    return (
        <div className={`flex flex-col bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700 ${className}`}>
            {header && (
                <div className="border-b border-gray-200 dark:border-slate-700">
                    {header}
                </div>
            )}

            {/* Building dropdown — matches the Planning view's equivalent. */}
            <div className="p-3 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/40">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                    Building filter
                </label>
                <select
                    value={buildingFilter}
                    onChange={(e) => {
                        setBuildingFilter(e.target.value);
                        selectRoot();
                    }}
                    disabled={!projectId || loading}
                    className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded px-2 py-1.5 text-sm"
                >
                    <option value="All">All buildings</option>
                    {tree
                        .filter((b) => (b.stats?.total ?? 0) > 0)
                        .map((b) => (
                            <option key={b.id} value={b.name}>{b.name}</option>
                        ))}
                </select>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
                {!projectId ? (
                    <div className="p-4 text-xs text-gray-500 dark:text-gray-400">
                        Select a project to browse its structure.
                    </div>
                ) : loading && tree.length === 0 ? (
                    <div className="p-4 text-xs text-gray-500 dark:text-gray-400">Loading…</div>
                ) : error ? (
                    <div className="p-4 text-xs text-red-600 dark:text-red-400">{error}</div>
                ) : (
                    <>
                        {/* Virtual "root" row — clears any in-flight location filter. */}
                        <div
                            role="button"
                            onClick={selectRoot}
                            className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer text-sm mb-1 ${
                                !selectedKey
                                    ? 'bg-blue-50 dark:bg-neutral-800 text-blue-700 dark:text-neutral-100'
                                    : 'hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200'
                            }`}
                        >
                            <FileText className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <span className="flex-1 truncate font-medium">
                                {buildingFilter === 'All' ? 'All buildings' : buildingFilter}
                            </span>
                        </div>

                        {filteredTree.map((building) => (
                            <TreeRow
                                key={building.id}
                                node={building}
                                nodeKey={building.id}
                                level={0}
                                parentFilters={{}}
                                parentPath={['All Buildings']}
                                expanded={expanded}
                                onToggle={toggle}
                                selectedKey={selectedKey}
                                onSelect={onSelect}
                            />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}

// ----- recursive row --------------------------------------------------------

interface TreeRowProps {
    node: ProjectTreeNode;
    nodeKey: string;
    level: number;
    parentFilters: TreeNodeFilters;
    parentPath: string[];
    expanded: Record<string, boolean>;
    onToggle: (key: string) => void;
    selectedKey: string | null | undefined;
    onSelect: ProjectTreePanelProps['onSelect'];
}

function TreeRow({
    node,
    nodeKey,
    level,
    parentFilters,
    parentPath,
    expanded,
    onToggle,
    selectedKey,
    onSelect,
}: TreeRowProps) {
    const isExpanded = expanded[nodeKey] ?? level === 0;
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const Icon = ICONS[node.type] || Box;
    const path = useMemo(() => [...parentPath, node.name], [parentPath, node.name]);
    const filters = useMemo(() => buildFilters(node, parentFilters), [node, parentFilters]);
    const isSelected = selectedKey === nodeKey;

    return (
        <div className="select-none">
            <div
                role="button"
                onClick={() => onSelect(nodeKey, filters, path)}
                className={`flex items-center gap-1 py-1.5 pr-2 rounded cursor-pointer text-sm ${
                    isSelected
                        ? 'bg-blue-50 dark:bg-neutral-800 text-blue-700 dark:text-neutral-100'
                        : 'hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200'
                }`}
                style={{ paddingLeft: 6 + level * 14 }}
            >
                {hasChildren ? (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onToggle(nodeKey); }}
                        className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-slate-600"
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                        {isExpanded
                            ? <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                            : <ChevronRight className="w-3.5 h-3.5 text-gray-500" />}
                    </button>
                ) : (
                    <span className="w-4" />
                )}
                <Icon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 shrink-0" />
                <span className="flex-1 truncate">{node.name}</span>
                <MilestoneBadge stats={node.milestoneStats} />
                <span className="text-[11px] text-gray-500 dark:text-gray-400 shrink-0">
                    {node.stats?.total ?? 0}
                </span>
            </div>

            {isExpanded && hasChildren && node.children!.map((child) => (
                <TreeRow
                    key={child.id}
                    node={child}
                    nodeKey={`${nodeKey}.${child.id}`}
                    level={level + 1}
                    parentFilters={filters}
                    parentPath={path}
                    expanded={expanded}
                    onToggle={onToggle}
                    selectedKey={selectedKey}
                    onSelect={onSelect}
                />
            ))}
        </div>
    );
}
